import { Fragment, useRef, useState } from "react";
import { Link } from "react-router";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn, GridCellProps } from "@progress/kendo-react-grid";
import UserName from "src/dm/components/UserName/UserName";
import {
  AttachmentTypeRow,
  downloadAttachmentUrl,
  useAttachmentTypes,
  useDealAttachments,
  useDeleteAttachment,
  useUpdateAttachmentDescription,
  useUploadAttachment,
} from "src/dm/hooks/useAttachments";
import { DealAttachment } from "src/dm/interfaces/DealAttachment";
import * as s from "./DealAttachments.css";

// File-extension whitelist + 5 MB cap. Legacy AttachmentDocs.aspx hard-coded
// these (see audit 12 §3). Mirrored here so the dialog can validate before
// firing the multipart POST.
const ALLOWED = [
  "jpg",
  "jpeg",
  "gif",
  "png",
  "txt",
  "rtf",
  "doc",
  "docx",
  "ppt",
  "zip",
  "pdf",
  "eps",
  "xls",
  "xlsx",
];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;
const DESCRIPTION_MAX = 60;

const fmtSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const fmtWhen = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

interface Props {
  dealId: number;
}

const DealAttachments = ({ dealId }: Props) => {
  const { data, isLoading, isError, error } = useDealAttachments(dealId);
  const typesQ = useAttachmentTypes();
  const upload = useUploadAttachment(dealId);
  const updateDesc = useUpdateAttachmentDescription(dealId);
  const remove = useDeleteAttachment(dealId);

  // Inline-edit state — Description is the only "fast edit" affordance, so
  // it stays as an in-row Input + Save/Cancel.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingDescription, setEditingDescription] = useState("");

  // Upload dialog state. Lives on this component (rather than at page
  // scope) because the dialog uses hooks that are already wired here.
  const fileRef = useRef<HTMLInputElement>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [selectedType, setSelectedType] = useState<AttachmentTypeRow | null>(
    null
  );
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Delete-confirm state — mirrors the AdminLookupPage Kendo Dialog pattern
  // (audit 22 §MINOR #8 — drop native confirm() for consistency).
  const [pendingDelete, setPendingDelete] = useState<DealAttachment | null>(
    null
  );

  const resetUploadForm = () => {
    setPickedFile(null);
    setDescription("");
    setSelectedType(null);
    setUploadError(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const openUpload = () => {
    resetUploadForm();
    setShowUpload(true);
  };

  const closeUpload = () => {
    setShowUpload(false);
    resetUploadForm();
  };

  const handleUpload = async () => {
    setUploadError(null);
    if (!pickedFile) return setUploadError("Pick a file first.");
    if (pickedFile.size > MAX_SIZE_BYTES)
      return setUploadError("File exceeds the 5 MB cap.");
    const ext = pickedFile.name.split(".").pop()?.toLowerCase() ?? "";
    if (!ALLOWED.includes(ext))
      return setUploadError(
        `Extension .${ext} not allowed. Allowed: ${ALLOWED.join(", ")}`
      );
    if (!description.trim())
      return setUploadError("Description is required.");
    if (description.length > DESCRIPTION_MAX)
      return setUploadError(
        `Description must be ${DESCRIPTION_MAX} characters or fewer.`
      );
    if (!selectedType)
      return setUploadError("Attachment type is required.");
    try {
      await upload.mutateAsync({
        file: pickedFile,
        description: description.trim(),
        attachmentTypeId: selectedType.id,
      });
      closeUpload();
    } catch (e) {
      const err = e as {
        response?: { data?: { errorDetails?: { exceptionMessage?: string } } };
        message?: string;
      };
      const msg =
        err?.response?.data?.errorDetails?.exceptionMessage ??
        err?.message ??
        "Upload failed";
      setUploadError(msg);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync(pendingDelete.attachmentId);
    } finally {
      setPendingDelete(null);
    }
  };

  // ----- Grid cell renderers -----------------------------------------------

  const FileNameCell = (props: GridCellProps) => {
    const a = props.dataItem as DealAttachment;
    return (
      <td>
        <a
          href={downloadAttachmentUrl(dealId, a.attachmentId)}
          target="_blank"
          rel="noopener noreferrer"
          className={s.itemLink}
          title="Download"
        >
          {a.fileName}
        </a>
        {a.dealItemId != null && (
          <div>
            <Link
              to={`/dm/deals/${dealId}/items/${a.dealItemId}`}
              className={s.itemLink}
            >
              Item #{a.dealItemId}
            </Link>
          </div>
        )}
      </td>
    );
  };

  const DescriptionCell = (props: GridCellProps) => {
    const a = props.dataItem as DealAttachment;
    const isEditing = editingId === a.attachmentId;
    if (isEditing) {
      return (
        <td>
          <div className={s.inlineEdit}>
            <Input
              className={s.inlineEditInput}
              value={editingDescription}
              maxLength={DESCRIPTION_MAX}
              onChange={(e) =>
                setEditingDescription(String(e.target.value ?? ""))
              }
            />
            <Button
              type="button"
              size="small"
              themeColor="primary"
              disabled={updateDesc.isPending}
              onClick={async () => {
                await updateDesc.mutateAsync({
                  attachmentId: a.attachmentId,
                  description: editingDescription.trim(),
                });
                setEditingId(null);
              }}
            >
              Save
            </Button>
            <Button
              type="button"
              size="small"
              onClick={() => setEditingId(null)}
            >
              Cancel
            </Button>
          </div>
        </td>
      );
    }
    return <td>{a.description || "—"}</td>;
  };

  const TypeCell = (props: GridCellProps) => {
    const a = props.dataItem as DealAttachment;
    const row = typesQ.data?.find((t) => t.id === a.attachmentTypeId);
    return <td>{row?.description ?? `#${a.attachmentTypeId}`}</td>;
  };

  const SizeCell = (props: GridCellProps) => {
    const a = props.dataItem as DealAttachment;
    return <td>{fmtSize(a.fileSize)}</td>;
  };

  const UploadedByCell = (props: GridCellProps) => {
    const a = props.dataItem as DealAttachment;
    return (
      <td>
        <UserName userId={a.whoCreated} />
      </td>
    );
  };

  const UploadedOnCell = (props: GridCellProps) => {
    const a = props.dataItem as DealAttachment;
    return <td>{fmtWhen(a.whenCreated)}</td>;
  };

  const ActionsCell = (props: GridCellProps) => {
    const a = props.dataItem as DealAttachment;
    const isEditing = editingId === a.attachmentId;
    if (isEditing) {
      // Save/Cancel live in the Description cell while editing — keep the
      // Actions column quiet to avoid double affordances.
      return <td />;
    }
    return (
      <td>
        <div className={s.actionsCell}>
          <Button
            type="button"
            size="small"
            fillMode="flat"
            onClick={() => {
              setEditingId(a.attachmentId);
              setEditingDescription(a.description ?? "");
            }}
          >
            Edit
          </Button>
          <Button
            type="button"
            size="small"
            fillMode="flat"
            themeColor="error"
            disabled={remove.isPending}
            onClick={() => setPendingDelete(a)}
          >
            Delete
          </Button>
        </div>
      </td>
    );
  };

  const count = data?.length ?? 0;

  return (
    <div className={s.wrap}>
      <div className={s.headerRow}>
        <h2 className={s.title}>
          Attachments
          <span className={s.counter}>({count})</span>
        </h2>
        <Button type="button" themeColor="primary" onClick={openUpload}>
          + Upload Attachment
        </Button>
      </div>

      <section className={s.card}>
        {isLoading && <p className={s.empty}>Loading attachments…</p>}
        {isError && (
          <p className={s.errorText}>
            {error instanceof Error ? error.message : "Failed to load"}
          </p>
        )}
        {!isLoading && !isError && count === 0 && (
          <p className={s.empty}>No attachments on this deal yet.</p>
        )}

        {count > 0 && (
          <Grid data={data}>
            <GridColumn title="File Name" cells={{ data: FileNameCell }} />
            <GridColumn title="Description" cells={{ data: DescriptionCell }} />
            <GridColumn title="Type" width="180px" cells={{ data: TypeCell }} />
            <GridColumn title="Size" width="100px" cells={{ data: SizeCell }} />
            <GridColumn
              title="Uploaded By"
              width="180px"
              cells={{ data: UploadedByCell }}
            />
            <GridColumn
              title="Uploaded On"
              width="180px"
              cells={{ data: UploadedOnCell }}
            />
            <GridColumn
              title=""
              width="160px"
              cells={{ data: ActionsCell }}
            />
          </Grid>
        )}
      </section>

      {showUpload && (
        <Dialog title="Upload attachment" onClose={closeUpload} width={560}>
          <div className={s.formGrid}>
            <Fragment>
              <label className={s.fieldLabel} htmlFor="att-file">
                File
              </label>
              <div className={s.inputCell}>
                <input
                  id="att-file"
                  ref={fileRef}
                  type="file"
                  accept={ALLOWED.map((e) => "." + e).join(",")}
                  onChange={(e) => setPickedFile(e.target.files?.[0] ?? null)}
                />
              </div>
            </Fragment>

            <Fragment>
              <label className={s.fieldLabel} htmlFor="att-desc">
                Description
              </label>
              <div className={s.inputCell}>
                <Input
                  id="att-desc"
                  value={description}
                  maxLength={DESCRIPTION_MAX}
                  onChange={(e) =>
                    setDescription(String(e.target.value ?? ""))
                  }
                  style={{ width: "100%" }}
                />
              </div>
            </Fragment>

            <Fragment>
              <label className={s.fieldLabel} htmlFor="att-type">
                Attachment Type
              </label>
              <div className={s.inputCell}>
                <DropDownList
                  id="att-type"
                  data={typesQ.data ?? []}
                  textField="description"
                  dataItemKey="id"
                  value={selectedType}
                  defaultItem={{
                    id: 0,
                    description: typesQ.isLoading
                      ? "Loading…"
                      : "-Select-",
                  }}
                  disabled={typesQ.isLoading}
                  onChange={(e) =>
                    setSelectedType(
                      e.value && e.value.id ? (e.value as AttachmentTypeRow) : null
                    )
                  }
                  style={{ width: "100%" }}
                />
              </div>
            </Fragment>

            <Fragment>
              <span />
              <p className={s.dialogHint}>
                Max 5 MB. Allowed: {ALLOWED.join(", ")}. Description up to{" "}
                {DESCRIPTION_MAX} characters.
              </p>
            </Fragment>
          </div>

          {uploadError && <p className={s.dialogError}>{uploadError}</p>}

          <DialogActionsBar>
            <Button type="button" onClick={closeUpload}>
              Cancel
            </Button>
            <Button
              type="button"
              themeColor="primary"
              onClick={handleUpload}
              disabled={upload.isPending}
            >
              {upload.isPending ? "Uploading…" : "Upload"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {pendingDelete && (
        <Dialog
          title="Confirm delete"
          onClose={() => setPendingDelete(null)}
          width={420}
        >
          <p style={{ margin: "0.25rem 0 0.75rem" }}>
            Delete attachment{" "}
            <code style={{ background: "#f1f5f9", padding: "0 0.25rem" }}>
              {pendingDelete.fileName}
            </code>
            ?
          </p>
          <p className={s.dialogHint}>This cannot be undone.</p>
          {remove.isError && (
            <p className={s.dialogError}>
              {remove.error instanceof Error
                ? remove.error.message
                : "Delete failed"}
            </p>
          )}
          <DialogActionsBar>
            <Button type="button" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              themeColor="error"
              onClick={confirmDelete}
              disabled={remove.isPending}
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default DealAttachments;
